import { useIntl } from '@/intl'
import {
  getTotalPriceForEquipmentsSelected,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/selectors'
import {
  CarListName,
  CarSheetContent,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/SheetViews'
import { editingCar, useCarRentalServiceStore } from '@/scopes/checkout/components/services/CarRentalFilteringService/store'
import { editCar } from '@/scopes/checkout/domain/actions'
import { createCarEvent, createEquipmentSelectionEvent } from '@/scopes/checkout/domain/analytics'
import { useService } from '@/scopes/checkout/domain/hooks'
import { type CheckoutCarRentalServiceOption, ServiceType } from '@/scopes/checkout/domain/types'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import {
  space,
  tok,
} from '@sunwebgroup/ui-components'
import { Banner, Button, InputStepper, ListItem, Sheet, Switch, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { css, styled } from 'styled-components'

export function getOptionEquipments(option: CheckoutCarRentalServiceOption | undefined, bookedEquipmentsWithQuantity: {
  id: string
  quantity: number
}[] | undefined) {
  if (!option)
    return []
  const { equipments, maxNumberOfEquipments } = option
  const equipmentsLimit = maxNumberOfEquipments ?? equipments.length
  return equipments?.map((equipment) => {
    const bookedEquipment = bookedEquipmentsWithQuantity?.find(bookedEquipmentWithQuantity => bookedEquipmentWithQuantity.id === equipment.id)
    return ({
      ...equipment,
      selected: Boolean(bookedEquipment),
      quantitySelected: bookedEquipment?.quantity ?? 0,
      isElegible: Boolean(bookedEquipmentsWithQuantity?.find(({ id }) => id === equipment.id) || ((bookedEquipmentsWithQuantity?.length || 0) < equipmentsLimit)),
    })
  }) ?? []
}

const EquipmentsFormFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: ${space('primitives_space_8')};
`
const EquipmentField = styled.div<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: ${space('primitives_space_8')};
  ${({ $disabled }) => $disabled && modDisabled()}
`

function modDisabled() {
  return css`
    opacity: ${tok('primitives_opacity_disabled')}%;
    pointer-events: none;
  `
}

const EquipmentFieldTrailing = styled.div``

const TotalEquipmentsPrice = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
`
const TotalEquipmentsPriceContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_4')};
`
const TotalEquipmentsPriceText = styled(Typography)`
  color: ${tok('semantics_content_color')};
`
const TotalEquipmentsPriceHintText = styled(Typography)`
  color: ${tok('semantics_content_secondary_color')};;
`

export function EquipmentsForm({ showEquipmentsForm, setShowEquipmentsForm, ...option }: CheckoutCarRentalServiceOption & { showEquipmentsForm: boolean, setShowEquipmentsForm: (value: boolean) => void }) {
  const { service, setServiceValue } = useService(ServiceType.CarRentalFiltering)
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { endEditingCar, toggleEquipmentEnquiry } = useCarRentalServiceStore()
  const { label, value: carValue, id: carId, driverId } = option
  const driver = driverId ? service?.participants[driverId] : null
  const currentEditingCar = useCarRentalServiceStore(editingCar(carId))
  const [totalEquipmentPrice, setTotalEquipmentPrice] = React.useState(getTotalPriceForEquipmentsSelected(option) ?? 0)

  const {
    bookedEquipmentsWithQuantity,
  } = currentEditingCar

  const availableEquipments = getOptionEquipments(option, bookedEquipmentsWithQuantity)

  React.useEffect(() => {
    const totalPrice = bookedEquipmentsWithQuantity?.reduce((acc, { id, quantity }) => {
      const equipment = option.equipments.find(({ id: equipmentId }) => equipmentId === id)
      if (!equipment)
        return acc
      const price = (equipment.price ?? 0) * quantity
      return acc + price
    }, 0)
    setTotalEquipmentPrice(totalPrice ?? 0)
  }, [showEquipmentsForm, option, bookedEquipmentsWithQuantity, driverId, carId, service?.participants])

  const handleEquipmentsQuantityChange = (value: string, quantity: number) => {
    toggleEquipmentEnquiry(carId, value, quantity)

    sendEvent(
      createEquipmentSelectionEvent({
        result: bookedEquipmentsWithQuantity.find(({ id }) => id === value) ? 'equipment_remove' : 'equipment_add',
        type: carValue,
        description: availableEquipments.find(equipment => equipment.id === value)?.text ?? '',
        element: 'input stepper',
        validated: true,
      }),
    )
  }

  const handleEquipmentsSwitchChange = (value: string, quantity: number) => {
    toggleEquipmentEnquiry(carId, value, quantity)
    sendEvent(
      createEquipmentSelectionEvent({
        result: bookedEquipmentsWithQuantity.find(({ id }) => id === value) ? 'equipment_remove' : 'equipment_add',
        type: carValue,
        description: availableEquipments.find(equipment => equipment.id === value)?.text ?? '',
        element: 'toggle',
        validated: true,
      }),
    )
  }

  const handleSaveValue
    = () => {
      if (!service)
        return
      if (!driver)
        return
      setServiceValue(editCar({
        value: carValue,
        id: carId,
        participantId: driverId ?? '',
        driverBirthDate: driver?.birthdate ? new Date(driver.birthdate) : new Date(),
        bookedEquipmentIds: bookedEquipmentsWithQuantity?.map(({ id }) => id) ?? [],
        bookedEquipmentsWithQuantity: bookedEquipmentsWithQuantity ?? [],
      })(service))

      endEditingCar(carId)
      setShowEquipmentsForm(false)

      sendEvent(
        createCarEvent({
          result: 'Car equipments selected',
          type: carValue,
          description: 'selectEquipments',
          element: 'button',
          validated: true,
        }),
      )
    }

  if (!service)
    return null
  if (!option)
    return null
  const lastEligibleId = availableEquipments?.filter(e => e.isElegible).at(-1)?.id
  const { equipments, maxNumberOfEquipments } = option
  const equipmentsLimit = maxNumberOfEquipments ?? equipments.length

  return (
    <>
      <Sheet.Content>
        <CarSheetContent>
          <CarListName $variant="display-h-regular">
            {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_car_model' }, { carModel: label })}
            {' '}
          </CarListName>
          <Typography $variant="body-m-regular-snug">
            {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_select_extras_description' }, { max: option?.maxNumberOfEquipments })}
          </Typography>
          {availableEquipments && availableEquipments.length > 0 && (
            <EquipmentsFormFieldContainer>
              { availableEquipments?.map(({ id, text, selected, price, maxQuantity, quantitySelected, isElegible }) => {
                const selectedEquipmentsLength = availableEquipments.filter(e => e.selected).length
                const isLastEligibleWithMaxReached
                  = isElegible
                    && id === lastEligibleId
                    && equipmentsLimit
                    && selectedEquipmentsLength >= equipmentsLimit
                return (
                  <React.Fragment key={id}>
                    <ListItem variant="outlined">
                      <EquipmentField $disabled={!isElegible}>
                        <Typography $variant="body-m-regular-tight">
                          {text}
                          {price ? `(${intl.formatCurrency(price)})` : ''}
                        </Typography>
                        <EquipmentFieldTrailing>
                          {maxQuantity && maxQuantity > 1 && (
                            <InputStepper
                              minimumValue={0}
                              maximumValue={maxQuantity}
                              initialValue={quantitySelected}
                              disabled={!isElegible}
                              size="M"
                              onChange={value => handleEquipmentsQuantityChange(id, value)}
                              decrementButtonAriaLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_equipments_decrement_button_aria_label' }, { equipmentName: text })}
                              incrementButtonAriaLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_equipments_increment_button_aria_label' }, { equipmentName: text })}
                              valueAriaLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_equipments_value_aria_label' }, { equipmentName: text })}
                            />
                          )}
                          {(!maxQuantity || maxQuantity === 1) && (
                            <Switch
                              defaultChecked={selected}
                              value={id}
                              disabled={!isElegible}
                              onCheckedChange={value => handleEquipmentsSwitchChange(id, value ? 1 : 0)}
                            />
                          )}
                        </EquipmentFieldTrailing>
                      </EquipmentField>
                    </ListItem>
                    {isLastEligibleWithMaxReached && (
                      <Banner
                        color="neutral"
                        hasIcon
                        size="M"
                        text={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_max_banner_text' })}
                        supportingText={intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_max_banner_supporting_text' }, { limit: equipmentsLimit })}
                      />
                    )}
                  </React.Fragment>
                )
              })}
            </EquipmentsFormFieldContainer>
          )}
          <ListItem variant="tinted">
            <TotalEquipmentsPrice>
              <TotalEquipmentsPriceContent>
                <TotalEquipmentsPriceText $variant="body-l-bold-tight">
                  {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_total_price' })}
                </TotalEquipmentsPriceText>
                <TotalEquipmentsPriceHintText $variant="body-s-regular-tight">
                  {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_equipments_total_price_hint_text' })}
                </TotalEquipmentsPriceHintText>
              </TotalEquipmentsPriceContent>
              <Typography $variant="body-l-bold-tight">{intl.formatCurrency(totalEquipmentPrice)}</Typography>
            </TotalEquipmentsPrice>

          </ListItem>
        </CarSheetContent>
      </Sheet.Content>
      <Sheet.Footer>
        <Button color="primary" $fullWidth onClick={handleSaveValue}>
          {intl.formatMessage({ id: 'checkout_steps_ancillaries_car_rental_filtering_form_driver_save_button_text' })}
        </Button>
      </Sheet.Footer>
    </>
  )
}
